export type UploadErrorKind =
  | "file_type"
  | "file_empty"
  | "file_too_large"
  | "row_limit"
  | "missing_columns"
  | "ingest_error"
  | "parse_error"
  | "schema_drift"
  | "unknown";

export interface SchemaDriftDetail {
  mapping: string;
  missing_columns: string[];
  added_columns: string[];
  missing_count: number;
  added_count: number;
}

export interface UploadError {
  kind: UploadErrorKind;
  message: string;
  missingColumns?: string[];
  hint?: string;
  schemaDrift?: SchemaDriftDetail;
}

export interface UploadSuccess {
  file: string;
  totalRows?: number;
  warnings: string[];
  preview?: unknown[];
  summary?: Record<string, unknown>;
}

export type UploadResult =
  | { ok: true; data: UploadSuccess }
  | { ok: "warned"; data: UploadSuccess }
  | { ok: false; error: UploadError };

function _classifyMessage(message: string): UploadErrorKind {
  const m = message.toLowerCase();
  if (m.includes("file type") || m.includes("extension") || m.includes("not allowed")) return "file_type";
  if (m.includes("empty") || m.includes("no headers") || m.includes("no data rows")) return "file_empty";
  if (m.includes("too large") || m.includes("maximum")) return "file_too_large";
  if (m.includes("row limit") || m.includes("rows detected") || m.includes("rows. maximum")) return "row_limit";
  return "unknown";
}

export function normalizeUploadResponse(
  status: number,
  body: Record<string, unknown>,
): UploadResult {
  // Fallback transitorio: HTTP 200 con status: "error" (pre-Fase 0)
  if (status === 200 && body.status === "error") {
    return {
      ok: false,
      error: {
        kind: "ingest_error",
        message: typeof body.error === "string" ? body.error : "Ingest error",
        hint: typeof body.hint === "string" ? body.hint : undefined,
      },
    };
  }

  if (status === 200) {
    const warnings: string[] = Array.isArray(body.warnings) ? (body.warnings as string[]) : [];
    const data: UploadSuccess = {
      file: typeof body.file === "string" ? body.file : "",
      totalRows: typeof body.total_rows === "number" ? body.total_rows : typeof body.rows === "number" ? body.rows : undefined,
      warnings,
      preview: Array.isArray(body.preview) ? body.preview : undefined,
      summary: body.summary && typeof body.summary === "object" ? (body.summary as Record<string, unknown>) : undefined,
    };
    return warnings.length > 0 ? { ok: "warned", data } : { ok: true, data };
  }

  const detail = body.detail;

  if (status === 413) {
    const msg = typeof detail === "string" ? detail : "File too large";
    const kind = _classifyMessage(msg);
    return { ok: false, error: { kind: kind === "unknown" ? "file_too_large" : kind, message: msg } };
  }

  if (status === 422) {
    if (detail && typeof detail === "object") {
      const d = detail as Record<string, unknown>;
      if (d.error === "missing_required_columns" && Array.isArray(d.missing_columns)) {
        return {
          ok: false,
          error: {
            kind: "missing_columns",
            message: "Required columns not found in file.",
            missingColumns: d.missing_columns as string[],
          },
        };
      }
    }
    return { ok: false, error: { kind: "missing_columns", message: "Required columns not found in file." } };
  }

  if (status === 400) {
    if (detail && typeof detail === "object") {
      const d = detail as Record<string, unknown>;
      if (d.error === "schema_drift" && d.schema_drift && typeof d.schema_drift === "object") {
        const sd = d.schema_drift as SchemaDriftDetail;
        return {
          ok: false,
          error: {
            kind: "schema_drift",
            message: typeof d.message === "string" ? d.message : "Column mapping outdated",
            hint: typeof d.hint === "string" ? d.hint : undefined,
            schemaDrift: sd,
          },
        };
      }
      const kind =
        d.error === "parse_error" ? "parse_error" :
        d.error === "file_empty" ? "file_empty" :
        "ingest_error";
      return {
        ok: false,
        error: {
          kind,
          message: typeof d.message === "string" ? d.message : "Upload error",
          hint: typeof d.hint === "string" ? d.hint : undefined,
        },
      };
    }
    const msg = typeof detail === "string" ? detail : "Upload error";
    return { ok: false, error: { kind: _classifyMessage(msg), message: msg } };
  }

  const fallbackMsg =
    typeof detail === "string"
      ? detail
      : typeof body.error === "string"
      ? body.error
      : `Upload error ${status}`;
  return { ok: false, error: { kind: "unknown", message: fallbackMsg } };
}
